import { useState, useEffect } from "react";
import {
  Typography,
  Box,
  Card,
  useTheme,
  useMediaQuery,
  Chip,
  IconButton,
  Fade,
  Dialog,
  DialogContent,
  DialogActions,
  CircularProgress,
  Button,
} from "@mui/material";
import {
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Description as DocumentIcon,
  Visibility as ViewIcon,
  Close as CloseIcon,
  CheckCircle as CheckIcon,
  Download as DownloadIcon,
  OpenInNew as OpenInNewIcon,
} from "@mui/icons-material";
import useAlert from "../../hooks/useAlert";
import {
  getFichaCadastral,
  assinarFichaCadastral,
} from "../../api/chamadasApi";
import { existFichaCadastral } from "../../api/Cadastro";
import { LocationPermissionModal } from "../LocationPermissionModal";
import { ButtonCustom } from "../Botoes/Button";
import { GeolocationService } from "../../services/geolocationService";
import { configurationVariables } from "../../environment/index";
export const FichaCadastral = ({
  ClienteId,
  onSave,
  idPerfilLimiteOperacional,
  isTelaCadastro = false,
}) => {
  const [pdfData, setPdfData] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hovered, setHovered] = useState(false);
  const theme = useTheme();
  const [hasFichaCadastral, setHasFichaCadastral] = useState(false);
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [isRequestingLocation, setIsRequestingLocation] = useState(false);
  const [loadingAssinatura, setLoadingAssinatura] = useState(false);
  const [showPdfFallback, setShowPdfFallback] = useState(false);
  const { showAlertSuccess, showAlertError } = useAlert();

  // Novos estados para a visualização do PDF
  const [viewerType, setViewerType] = useState("embedded");

  const checkLocationPermission = async () => {
    try {
      await GeolocationService.getBrowserGeolocation();
      return true;
    } catch (error) {
      return false;
    }
  };

  const handleSignature = async () => {
    setLoadingAssinatura(true);
    try {
      const hasPermission = await checkLocationPermission();
      if (hasPermission) {
        await proceedWithSignature();
      } else {
        setShowLocationModal(true);
      }
    } catch (error) {
      setLoadingAssinatura(false);
      showAlertError("Erro ao verificar permissão: " + error.message);
    }
  };

  const proceedWithSignature = async () => {
    try {
      setLoadingAssinatura(true);
      const locationData = await GeolocationService.getCompleteLocationData();
      await assinarFichaCadastral(ClienteId, locationData);

      onSave();
      showAlertSuccess("Ficha cadastral assinada com sucesso!");
    } catch (error) {
      console.error("Erro completo ao assinar:", error);
      showAlertError("Erro ao assinar ficha cadastral: " + error.message);
      setLoadingAssinatura(false);
    } finally {
      finalizarAssinatura();
    }
  };

  const finalizarAssinatura = () => {
    setLoading(false);
    setLoadingAssinatura(false);
    setShowLocationModal(false);
    setOpenModal(false);
  };

  const handleRequestPermission = async () => {
    setIsRequestingLocation(true);
    try {
      await GeolocationService.getBrowserGeolocation();
      setShowLocationModal(false);
      await proceedWithSignature();
    } catch (error) {
      await proceedWithSignature();
    } finally {
      setIsRequestingLocation(false);
    }
  };

  const getFichaCadastralExistente = async () => {
    try {
      const resp = await existFichaCadastral(
        ClienteId,
        idPerfilLimiteOperacional
      );
      setHasFichaCadastral(resp.data);
    } catch (err) {
      showAlertError("Erro ao carregar dados do cliente " + err);
    }
  };

  useEffect(() => {
    getFichaCadastralExistente();
  }, [ClienteId]);

  const initFichaCadastral = async () => {
    setLoading(true);
    try {
      const response = await getFichaCadastral(ClienteId);
      setOpenModal(true);
      setPdfData(response);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      showAlertError("Erro ao carregar a ficha cadastral " + error);
    }
  };

  const onCloseModal = () => {
    setOpenModal(false);
    setPdfData(null);
    setLoading(false);
    setShowLocationModal(false);
    setViewerType("embedded"); // Reset para visualização padrão
  };

  const handleDownload = () => {
    const a = document.createElement("a");
    a.href = pdfData;
    a.download = `ficha-cadastral-${ClienteId}.pdf`;
    a.click();
  };

  const handleOpenExternal = () => {
    window.open(pdfData, "_blank");
  };

  // Funções para renderizar o modal do PDF (COMPARTILHADO)
  const renderMobileOptions = () => (
    <Box
      sx={{
        textAlign: "center",
        p: 2,
        bgcolor: "grey.50",
        borderRadius: 2,
        mb: 2,
        border: "1px solid",
        borderColor: "divider",
      }}
    >
      <Typography variant="body2" gutterBottom fontWeight="600">
        Como deseja visualizar o PDF?
      </Typography>
      <Box
        sx={{
          display: "flex",
          gap: 1,
          justifyContent: "center",
          flexWrap: "wrap",
        }}
      >
        <Button
          variant={viewerType === "embedded" ? "contained" : "outlined"}
          size="small"
          onClick={() => setViewerType("embedded")}
          startIcon={<OpenInNewIcon />}
        >
          Visualizar Aqui
        </Button>
        <Button
          variant={viewerType === "external" ? "contained" : "outlined"}
          size="small"
          onClick={() => setViewerType("external")}
          startIcon={<OpenInNewIcon />}
        >
          Abrir Externo
        </Button>
        <Button
          variant={viewerType === "download" ? "contained" : "outlined"}
          size="small"
          onClick={() => setViewerType("download")}
          startIcon={<DownloadIcon />}
        >
          Baixar
        </Button>
      </Box>
    </Box>
  );

  const renderPdfContent = () => {
    switch (viewerType) {
      case "external":
        return (
          <Box sx={{ textAlign: "center", p: 3 }}>
            <Button
              variant="contained"
              onClick={handleOpenExternal}
              startIcon={<OpenInNewIcon />}
              sx={{ mb: 2 }}
            >
              Abrir PDF em Nova Janela
            </Button>
            <Typography variant="body2" color="text.secondary">
              O PDF será aberto em uma nova aba do seu navegador
            </Typography>
          </Box>
        );

      case "download":
        return (
          <Box sx={{ textAlign: "center", p: 3 }}>
            <Button
              variant="contained"
              onClick={handleDownload}
              startIcon={<DownloadIcon />}
              sx={{ mb: 2 }}
            >
              Baixar PDF
            </Button>
            <Typography variant="body2" color="text.secondary">
              O PDF será baixado para seu dispositivo
            </Typography>
          </Box>
        );

      case "embedded":
      default:
        if (showPdfFallback) {
          return (
            <Box sx={{ textAlign: "center", p: 3 }}>
              <Typography variant="body2" color="error" gutterBottom>
                Não foi possível visualizar o PDF diretamente
              </Typography>
              <Button
                variant="contained"
                onClick={handleOpenExternal}
                startIcon={<OpenInNewIcon />}
                sx={{ mb: 1 }}
              >
                Abrir em Nova Janela
              </Button>
              <Button
                variant="outlined"
                onClick={handleDownload}
                startIcon={<DownloadIcon />}
              >
                Baixar PDF
              </Button>
            </Box>
          );
        }

        return (
          <Box
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
              overflow: "hidden",
              width: "100%",
              height: isMobile ? "60vh" : "500px",
              minHeight: "400px",
              position: "relative",
            }}
          >
            <iframe
              src={pdfData}
              title="Visualizador de PDF"
              width="100%"
              height="100%"
              style={{ border: "none" }}
              onError={() => {
                setShowPdfFallback(true);
              }}
            />
            {/* Overlay de fallback para mobile */}
            {isMobile && (
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  backgroundColor: "rgba(0, 0, 0, 0.7)",
                  color: "white",
                  p: 2,
                  textAlign: "center",
                }}
                onClick={() => setViewerType("external")}
              >
                <OpenInNewIcon sx={{ fontSize: 40, mb: 1 }} />
                <Typography variant="body2" gutterBottom>
                  Clique para abrir o PDF
                </Typography>
                <Typography variant="caption">
                  O visualizador interno pode não funcionar em alguns
                  dispositivos
                </Typography>
              </Box>
            )}
          </Box>
        );
    }
  };

  const renderPdfModal = () => (
    <Dialog
      open={openModal}
      onClose={onCloseModal}
      maxWidth="lg"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 3,
          height: "90vh",
          maxHeight: 800,
          overflow: "hidden",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          p: 3,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Typography variant="h6" fontWeight="600" color="primary">
          Ficha Cadastral
        </Typography>
        <IconButton onClick={onCloseModal} size="small">
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent sx={{ p: 3, display: "flex", flexDirection: "column" }}>
        {pdfData ? (
          <>
            {isMobile && renderMobileOptions()}
            {renderPdfContent()}

            {/* Para desktop, mostrar opções de download sempre */}
            {!isMobile && (
              <Box
                sx={{
                  mt: 2,
                  display: "flex",
                  justifyContent: "center",
                  gap: 1,
                }}
              >
                <Button
                  variant="outlined"
                  onClick={handleOpenExternal}
                  startIcon={<OpenInNewIcon />}
                  size="small"
                >
                  Abrir Externo
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleDownload}
                  startIcon={<DownloadIcon />}
                  size="small"
                >
                  Baixar
                </Button>
              </Box>
            )}
          </>
        ) : (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              height: "300px",
            }}
          >
            <CircularProgress sx={{ mb: 2 }} />
            <Typography variant="body2" color="text.secondary">
              Carregando documento...
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions
        sx={{ p: 3, borderTop: `1px solid ${theme.palette.divider}` }}
      >
        <ButtonCustom
          onClick={onCloseModal}
          variant="outlined"
          disabled={loadingAssinatura}
          sx={{ borderRadius: 2, px: 3 }}
        >
          {loadingAssinatura ? (
            <CircularProgress size={16} color="inherit" />
          ) : (
            "Fechar"
          )}
        </ButtonCustom>
        <ButtonCustom
          onClick={handleSignature}
          variant="contained"
          disabled={
            loadingAssinatura || viewerType !== "embedded" || showPdfFallback
          }
          startIcon={<CheckIcon />}
          sx={{
            borderRadius: 2,
            px: 3,
            background:
              viewerType !== "embedded" || showPdfFallback
                ? "grey.300"
                : "linear-gradient(45deg, #1976d2 30%, #1565c0 90%)",
            "&:hover": {
              background:
                viewerType !== "embedded" || showPdfFallback
                  ? "grey.300"
                  : "linear-gradient(45deg, #1565c0 30%, #0d47a1 90%)",
            },
          }}
        >
          {loadingAssinatura ? (
            <CircularProgress size={16} color="inherit" />
          ) : viewerType !== "embedded" || showPdfFallback ? (
            "Visualize o PDF primeiro"
          ) : (
            "Assinar"
          )}
        </ButtonCustom>
      </DialogActions>
    </Dialog>
  );

  // PRIMEIRO RETURN: Para quando NÃO é tela de cadastro E não tem ficha cadastral
  if (!isTelaCadastro && !hasFichaCadastral) {
    return (
      <>
        <Card
          elevation={hovered ? 4 : 1}
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
          sx={{
            borderRadius: 3,
            p: 1.5,
            mb: 2,
            background: "linear-gradient(135deg, #f0f4ff 0%, #e3f2fd 100%)",
            border: `1px solid ${theme.palette.primary.light}`,
            transition: "all 0.3s ease",
            cursor: "pointer",
            "&:hover": {
              transform: "translateY(-2px)",
            },
          }}
          onClick={initFichaCadastral}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box
                sx={{
                  p: 1,
                  borderRadius: "50%",
                  bgcolor: "primary.main",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 40,
                  height: 40,
                }}
              >
                <DocumentIcon sx={{ color: "white", fontSize: 20 }} />
              </Box>
              <Box>
                <Typography variant="subtitle1" fontWeight="600">
                  Ficha Cadastral
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Visualize e assine seu documento
                </Typography>
              </Box>
            </Box>
            {loading ? (
              <CircularProgress size={24} color="primary" />
            ) : (
              <Chip
                icon={<ViewIcon />}
                label="Visualizar"
                size="small"
                color="primary"
                variant={"filled"}
                sx={{ borderRadius: 2, fontWeight: 500 }}
              />
            )}
          </Box>
        </Card>

        <LocationPermissionModal
          isOpen={showLocationModal}
          onClose={() => setShowLocationModal(false)}
          onPermissionGranted={handleRequestPermission}
          isRequesting={isRequestingLocation}
        />

        {renderPdfModal()}
      </>
    );
  }

  // SEGUNDO RETURN: Para quando É tela de cadastro OU já tem ficha cadastral
  return (
    <>
      {!hasFichaCadastral && (
        <Fade in timeout={800}>
          <Card
            elevation={2}
            sx={{
              borderRadius: 3,
              p: 3,
              mt: 3,
              background: "white",
              border: `1px solid ${theme.palette.divider}`,
              position: "relative",
              overflow: "hidden",
            }}
          >
            <Box sx={{ position: "relative", zIndex: 1 }}>
              <Box
                sx={{ display: "flex", alignItems: "center", mb: 3, gap: 2 }}
              >
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: "50%",
                    bgcolor: "primary.main",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 48,
                    height: 48,
                    flexShrink: 0,
                  }}
                >
                  <DocumentIcon sx={{ color: "white", fontSize: 24 }} />
                </Box>
                <Typography variant="h6" fontWeight="600" color="primary">
                  Ficha Cadastral
                </Typography>
              </Box>

              <Card
                variant="outlined"
                sx={{
                  p: 2.5,
                  backgroundColor: "grey.50",
                  borderColor: "primary.light",
                  borderRadius: 2,
                  mb: 3,
                }}
              >
                <Typography
                  variant="body1"
                  paragraph
                  fontWeight="800"
                  sx={{ mb: 2 }}
                >
                  Quase lá! Falta só assinar sua ficha cadastral
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ lineHeight: 1.6 }}
                >
                  Para finalizar o processo, é necessário assinar
                  eletronicamente sua ficha cadastral. Esta etapa é obrigatória
                  para liberar o acesso completo à plataforma.
                </Typography>
              </Card>

              <Box
                sx={{ mt: 3, display: "flex", gap: 2, alignItems: "center" }}
              >
                <ButtonCustom
                  onClick={initFichaCadastral}
                  variant="contained"
                  sx={{
                    borderRadius: 2,
                    px: 3,
                    py: 1.5,
                    fontWeight: 600,
                    minWidth: 240,
                  }}
                  disabled={loading}
                >
                  {loading ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    "Visualizar Documento"
                  )}
                </ButtonCustom>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ maxWidth: 200 }}
                >
                  Clique para visualizar e assinar sua ficha cadastral
                </Typography>
              </Box>
            </Box>

            <LocationPermissionModal
              isOpen={showLocationModal}
              onClose={() => setShowLocationModal(false)}
              onPermissionGranted={handleRequestPermission}
              isRequesting={isRequestingLocation}
            />

            {renderPdfModal()}
          </Card>
        </Fade>
      )}
    </>
  );
};
